import { memo, FC, useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Formik, Form, Field } from 'formik';
import { Button } from '@mui/material';
import { Clear, Check } from '@mui/icons-material';
import { ApartmentSelectField } from '@features/apartment-select';
import { ComplexSelectField } from '@features/complex-select';
import { EmployeeSelectField } from '@features/employee-select';
import { HouseSelectField } from '@features/house-select';
import { AsyncPeopleSelect } from '@features/people-async-select';
import { PropertySelectField } from '@features/property-select';
import { ClassSelectField } from '@features/tickets-class-select';
import { InvoicesStatusTypeSelectField } from '@features/tickets-invoices-status-select';
import { TypeSimpleTreeField } from '@features/tickets-new-type-simple-tree';
import { TicketsPaymentTypeSelectField } from '@features/tickets-payment-type-select';
import { PrioritySelectField } from '@features/tickets-priority-select';
import { StatusSelectField } from '@features/tickets-status-select';
import {
  Wrapper,
  FilterToolbar,
  CustomScrollbar,
  InputField,
  Divider,
  SelectField,
  SwitchField,
  ActionButton,
  BaseInputLabel,
} from '@ui/index';
import { Value } from '../../interfaces';
import { changedFilterVisibility, filtersSubmitted, $filters } from '../../models';
import { $setFiltersCounter } from '../../models/filters/filters.model';
import { RateSelectField } from '../rate-select';
import { SlaSelectField } from '../sla-select';
import { StuffWriteSelectField } from '../stuff-write-select';
import { useStyles } from './styles';
import { validationSchema } from './validation';

const PeopleField = (props: object) => (
  <SelectField component={<AsyncPeopleSelect enterprise_id={null} />} {...props} />
);

export const Filters: FC = memo(() => {
  const [filters] = useUnit([$filters]);
  const classes = useStyles();

  const onClose = () => changedFilterVisibility(false);
  const onReset = () => filtersSubmitted('');

  return (
    <Wrapper className={classes.wrapper}>
      <FilterToolbar className={classes.toolbar} closeFilter={onClose} />
      <Formik
        initialValues={filters}
        onSubmit={filtersSubmitted}
        onReset={onReset}
        enableReinitialize
        validationSchema={validationSchema}
        render={({ values, setFieldValue, handleSubmit, handleReset, setValues }) => (
          <FiltersBody
            values={values}
            handleSubmit={handleSubmit}
            handleReset={handleReset}
            setFieldValue={setFieldValue}
            setValues={setValues}
          />
        )}
      />
    </Wrapper>
  );
});

export const FiltersBody = ({
  values,
  handleSubmit,
  handleReset,
  setFieldValue,
  setValues,
}: {
  values: any;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
  handleReset: () => void;
  setValues: (values: any) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [filters, setFiltersCounter] = useUnit([$filters, $setFiltersCounter]);
  const classes = useStyles();
  const [typesShowAcrchived, setTypesShowAcrchived] = useState(false);

  const handlePayment = (value: Value) => {
    setFieldValue('payment', value);
    setFieldValue('invoiceStatus', '');
  };

  useEffect(() => {
    const lastFilters = structuredClone(filters);
    setTimeout(() => {
      setValues(lastFilters);
    }, 300);
  }, [setFiltersCounter]);

  return (
    <>
      <CustomScrollbar autoHide>
        <Form
          key={setFiltersCounter}
          className={classes.form}
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          <>
            <p className={classes.title}>{t('DateOfCreation')}</p>
            <div className={classes.dateField}>
              <div>
                <Field
                  name="created_at_from"
                  label={null}
                  divider={false}
                  component={InputField}
                  placeholder=""
                  type="date"
                />
              </div>
              <p className={classes.dateDivider}>-</p>
              <div>
                <Field
                  name="created_at_to"
                  label={null}
                  divider={false}
                  component={InputField}
                  placeholder=""
                  type="date"
                />
              </div>
            </div>
            <Divider />
          </>
          <Field
            name="statuses"
            component={StatusSelectField}
            label={t('RequestStatus')}
            placeholder={t('SelectRequestStatus')}
            isMulti
            divider={false}
          />
          <div className={classes.supportField}>
            <Field
              label={t('TransferredToTehzor')}
              component={SwitchField}
              name="isTehzor"
              labelPlacement="end"
              divider={false}
            />
            <Field
              label={t('OnTheTechnicalSupportSide')}
              component={SwitchField}
              name="isSupportActive"
              labelPlacement="end"
            />
          </div>
          <Field
            name="priorities"
            component={PrioritySelectField}
            label={t('ExecutionPriority')}
            placeholder={t('ChooseAPriority')}
            isMulti
          />
          <Field
            name="classes"
            component={ClassSelectField}
            label={t('RequestClass')}
            placeholder={t('SelectRequestClass')}
            isMulti
          />

          <div className={classes.typesFiledHeader}>
            <BaseInputLabel>{t('RequestType')}</BaseInputLabel>
            <Button variant="text" onClick={() => setTypesShowAcrchived((prev) => !prev)}>
              {typesShowAcrchived ? t('HideArchived') : t('ShowArchived')}
            </Button>
          </div>

          <Field
            id={'types-filter'}
            name="types"
            component={TypeSimpleTreeField}
            value={values.types}
            isFilter
            withAcrchived
            showAcrchived={typesShowAcrchived}
          />

          <Field
            name="payment"
            label={t('Payment')}
            placeholder={t('SelectPayment')}
            component={TicketsPaymentTypeSelectField}
            divider={values?.payment?.id !== 1}
            onChange={handlePayment}
          />
          {typeof values?.payment === 'object' && values.payment?.id === 1 && (
            <div className={classes.invoiceField}>
              <Field
                name="invoiceStatus"
                label={null}
                placeholder={t('selectStatuse')}
                component={InvoicesStatusTypeSelectField}
              />
            </div>
          )}
          <Field
            name="objects"
            component={ComplexSelectField}
            label={t('AnObject')}
            placeholder={t('selectObject')}
            isMulti
          />
          <Field
            name="houses"
            complex={
              Array.isArray(values?.objects) &&
              values.objects.length > 0 &&
              values.objects
            }
            component={HouseSelectField}
            label={t('building')}
            placeholder={t('selectBbuilding')}
            isMulti
          />
          <Field
            name="apartments"
            building_ids={
              Array.isArray(values?.houses) &&
              values.houses.map((house: { id: number }) => house.id)
            }
            component={ApartmentSelectField}
            label={t('Label.flat')}
            placeholder={t('selectThatApartment')}
            isMulti
          />
          <Field
            name="property"
            houseList={values.houses}
            component={PropertySelectField}
            label={t('room')}
            placeholder={t('ChooseRoom')}
            isMulti
          />
          <Field
            label={t('Declarant')}
            placeholder={t('Placeholder.EnterFullName')}
            disableCache
            component={PeopleField}
            name="initiators"
            helpText={t('EnterAtLeastOneLetter')}
            isMulti
          />
          <Field
            name="stuff_write_off_needed"
            component={StuffWriteSelectField}
            label={t('WriteOffRequired')}
            placeholder={t('SpecifyTheNeedToWriteOff')}
          />
          <Field
            name="expired"
            component={SlaSelectField}
            label={t('SLAStandard')}
            placeholder={t('SpecifyTheSLAStandard')}
          />
          <Field
            label={t('Performers')}
            placeholder={t('SelectPerformers')}
            component={EmployeeSelectField}
            name="assignees"
            isMulti
            divider
          />
          <Field
            label={t('Rating')}
            placeholder={t('SelectRating')}
            component={RateSelectField}
            name="rates"
            isMulti
            divider
          />
          <Field
            label={t('HavingUnreadMessages')}
            component={SwitchField}
            name="isUnreadMessage"
            labelPlacement="end"
          />
          <Field
            label={t('HasRelatedTicketsFilter')}
            component={SwitchField}
            name="has_related"
            labelPlacement="end"
          />
        </Form>
      </CustomScrollbar>
      <div className={classes.actions}>
        <ActionButton kind="negative" onClick={handleReset}>
          <Clear />
          {t('Reset')}
        </ActionButton>
        <ActionButton kind="positive" onClick={handleSubmit}>
          <Check />
          {t('Apply')}
        </ActionButton>
      </div>
    </>
  );
};
